import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Building2, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";

const inputCls = "w-full border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-[#002FA7]";

export default function CorporateApply() {
  const [form, setForm] = useState({ payment_preference: "invoice" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accept_terms) { toast.message("Please accept the terms to apply."); return; }
    setBusy(true);
    try {
      const payload = {
        company_name: form.company_name, trading_name: form.trading_name || null,
        contact_name: form.contact_name, email: form.email, phone: form.phone,
        address: form.address || null,
        est_monthly_trips: form.est_monthly_trips ? parseInt(form.est_monthly_trips, 10) : null,
        est_monthly_spend: form.est_monthly_spend ? parseFloat(form.est_monthly_spend) : null,
        payment_preference: form.payment_preference, notes: form.notes || null,
        accept_terms: !!form.accept_terms,
      };
      await api.post("/corporate/apply", payload);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not submit application.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-border p-8 text-center" data-testid="corporate-apply-success">
          <CheckCircle2 className="w-12 h-12 text-[#10B981] mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-black mb-2">Application received</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Thanks for your interest. Our team will review your application and email you shortly with the next steps.
          </p>
          <Link to="/" className="text-sm font-bold text-[#002FA7] hover:underline" data-testid="corporate-apply-home">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] py-10 px-6">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#0A0A0A] mb-4" data-testid="corporate-apply-back">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>
        <div className="bg-white border border-border">
          <div className="px-6 md:px-8 py-6 border-b border-border bg-white">
            <div className="flex items-center gap-2 text-[#002FA7]">
              <Building2 className="w-5 h-5" />
              <span className="label-mini">CORPORATE ACCOUNTS</span>
            </div>
            <h1 className="font-heading text-3xl font-black tracking-tight mt-1">Open a corporate account</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Give your team on-account travel with consolidated billing. Tell us about your business and we'll get you set up.
            </p>
          </div>
          <form onSubmit={submit} className="p-6 md:p-8 space-y-4" data-testid="corporate-apply-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Company name *"><Input required value={form.company_name || ""} onChange={set("company_name")} className={inputCls} placeholder="Acme Logistics Ltd" data-testid="apply-company-name" /></Field>
              <Field label="Trading name (optional)"><Input value={form.trading_name || ""} onChange={set("trading_name")} className={inputCls} placeholder="Acme" data-testid="apply-trading-name" /></Field>
              <Field label="Contact person *"><Input required value={form.contact_name || ""} onChange={set("contact_name")} className={inputCls} placeholder="Jane Owner" data-testid="apply-contact-name" /></Field>
              <Field label="Email *"><Input required type="email" value={form.email || ""} onChange={set("email")} className={inputCls} placeholder="jane@acme.com" data-testid="apply-email" /></Field>
              <Field label="Phone *"><Input required value={form.phone || ""} onChange={set("phone")} className={inputCls} placeholder="+44 7700 900000" data-testid="apply-phone" /></Field>
              <Field label="Company address"><Input value={form.address || ""} onChange={set("address")} className={inputCls} placeholder="1 High Street, Townville" data-testid="apply-address" /></Field>
              <Field label="Estimated monthly trips"><Input type="number" min="0" value={form.est_monthly_trips || ""} onChange={set("est_monthly_trips")} className={inputCls} placeholder="120" data-testid="apply-trips" /></Field>
              <Field label="Estimated monthly spend (£)"><Input type="number" min="0" step="0.01" value={form.est_monthly_spend || ""} onChange={set("est_monthly_spend")} className={inputCls} placeholder="3500" data-testid="apply-spend" /></Field>
            </div>
            <Field label="How would you like to pay?">
              <div className="flex flex-wrap gap-2" data-testid="apply-payment-pref">
                {[["invoice", "Monthly invoice"], ["card", "Card"], ["cash_account", "Cash account"]].map(([v, lbl]) => (
                  <button type="button" key={v} onClick={() => set("payment_preference")(v)}
                    className={`px-4 py-2 text-sm font-bold border transition-colors ${form.payment_preference === v ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-border hover:bg-secondary"}`}
                    data-testid={`apply-pay-${v}`}>{lbl}</button>
                ))}
              </div>
            </Field>
            <Field label="Notes / requirements">
              <textarea value={form.notes || ""} onChange={set("notes")} rows={3} className={`${inputCls} resize-none`} placeholder="e.g. regular airport runs for staff, cost centres needed later…" data-testid="apply-notes" />
            </Field>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!form.accept_terms} onChange={(e) => set("accept_terms")(e.target.checked)} className="mt-1" data-testid="apply-terms" />
              <span>I accept the terms of service and confirm I'm authorised to open an account for this company. *</span>
            </label>
            <Button type="submit" disabled={busy} className="w-full bg-[#002FA7] hover:bg-[#002685] text-white font-bold h-11 flex items-center justify-center gap-2" data-testid="apply-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Submit application
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <Label className="label-mini text-muted-foreground mb-1 block">{label}</Label>
    {children}
  </label>
);
